import { Static, Type } from '@sinclair/typebox';
import { PaymentInfosSchema, TrackingItemSchema } from './payment_infos';
import { BookingAmountsSchema } from './booking_amounts';
import {
  BookableAvailabilityResponseSchema,
  BookableSlotItemSchema,
  IncludeSessionSlotsBelowPartySizeSchema,
} from './availabilities';
import { CatalogSelectionInputSchema } from './catalog-selection';
import { CroppableImageResponseSchema } from './image';
import { AddressSchema, AppointmentRideDetailsResponseSchema } from './professional';
import { BookingCancellationRefundInfoSchema } from './pro_payment';
import { ReservationStatus } from './translation_namespace/availabilities_namespace';
import { PublicBookingSourceSchema } from './translation_namespace/booking_source_namespace';
import { PriceTypeSchema } from './translation_namespace/price_namespace';
import { PrestationPaymentType, PrestationPaymentTypeSchema } from './translation_namespace/prestation_namespace';
import { ProPaymentStatus } from './translation_namespace/pro_payment_namespace';
import { BookingCustomFieldAnswerSchema } from './booking_custom_field';
import { BookingInstructionPublicSchema } from './booking_instruction';
import { BookingPromoIneligibilityReason } from './translation_namespace/pro_promo_code_namespace';

export { PrestationPaymentType };

// ─── Request ──────────────────────────────────────────────────────────────────

export const PreReservationCustomerSchema = Type.Object({
  firstname: Type.String({
    maxLength: 100,
    title: 'INPUT_TITLE_PRE_RESERVATION_CUSTOMER_FIRSTNAME',
    placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_CUSTOMER_FIRSTNAME',
  }),
  lastname: Type.String({
    maxLength: 100,
    title: 'INPUT_TITLE_PRE_RESERVATION_CUSTOMER_LASTNAME',
    placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_CUSTOMER_LASTNAME',
  }),
  email: Type.String({
    format: 'email',
    maxLength: 255,
    title: 'INPUT_TITLE_PRE_RESERVATION_CUSTOMER_EMAIL',
    placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_CUSTOMER_EMAIL',
  }),
  phone: Type.Optional(
    Type.String({
      maxLength: 20,
      meta: { input_type: 'phone_number' },
      title: 'INPUT_TITLE_PRE_RESERVATION_CUSTOMER_PHONE',
      placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_CUSTOMER_PHONE',
    }),
  ),
});
export type PreReservationCustomerObject = Static<typeof PreReservationCustomerSchema>;

export const PreReservationSlotSchema = Type.Object({
  slot_start: Type.String({ format: 'date-time' }),
  slot_end: Type.String({ format: 'date-time' }),
  team_member_id: Type.Optional(Type.Number()),
  team_member_ids: Type.Optional(Type.Array(Type.Number())),
  pro_place_id: Type.Number(),
  ...CatalogSelectionInputSchema.properties,
});
export type PreReservationSlotObject = Static<typeof PreReservationSlotSchema>;

export const PreReservationRequestSchema = Type.Object({
  slots: Type.Array(PreReservationSlotSchema, {
    minItems: 1,
    maxItems: 20,
  }),
  customer: PreReservationCustomerSchema,
  // Même borne que `promo_code` : le champ unique du tunnel essaie d'abord le code saisi comme carte
  // cadeau, et une borne plus courte rejetait un code promo long en validation, sans essai du promo.
  voucher_code: Type.Optional(
    Type.String({
      maxLength: 64,
      title: 'INPUT_TITLE_PRE_RESERVATION_VOUCHER_CODE',
      placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_VOUCHER_CODE',
    }),
  ),
  promo_code: Type.Optional(
    Type.String({
      maxLength: 64,
      title: 'INPUT_TITLE_PRE_RESERVATION_PROMO_CODE',
      placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_PROMO_CODE',
    }),
  ),
  note: Type.Optional(
    Type.String({
      maxLength: 2000,
      meta: { multiline: true },
      title: 'INPUT_TITLE_PRE_RESERVATION_NOTE',
      placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_NOTE',
    }),
  ),
  customer_address: Type.Optional(AddressSchema),
  custom_field_answers: Type.Optional(Type.Array(BookingCustomFieldAnswerSchema)),
  // Canal d'entrée déclaré par le client HTTP (site, widget, lien direct…) ; les canaux internes
  // (agenda, assistant, import) sont refusés ici. Absent ⇒ ONLINE. cf. booking_source_namespace.ts.
  source: Type.Optional(PublicBookingSourceSchema),
  // Campagne email/SMS à l'origine de la réservation (lien marqué `cid`), vérifiée côté serveur
  // (campagne du pro, envoyée) et ignorée sans erreur sinon.
  campaign_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type PreReservationRequestObject = Static<typeof PreReservationRequestSchema>;

export const UpdatePreReservationVoucherRequestSchema = Type.Object({
  // Même borne que `promo_code` (cf. PreReservationRequestSchema).
  voucher_code: Type.Optional(
    Type.String({
      maxLength: 64,
      title: 'INPUT_TITLE_PRE_RESERVATION_VOUCHER_CODE',
      placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_VOUCHER_CODE',
    }),
  ),
});
export type UpdatePreReservationVoucherRequestObject = Static<typeof UpdatePreReservationVoucherRequestSchema>;

// Applique / retire un code promo sur une pré-réservation existante (code absent = retrait).
export const UpdatePreReservationPromoRequestSchema = Type.Object({
  promo_code: Type.Optional(
    Type.String({
      maxLength: 64,
      title: 'INPUT_TITLE_PRE_RESERVATION_PROMO_CODE',
      placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_PROMO_CODE',
    }),
  ),
});
export type UpdatePreReservationPromoRequestObject = Static<typeof UpdatePreReservationPromoRequestSchema>;

// Champ unique « code promo ou carte cadeau » d'une pré-réservation existante : le serveur reconnaît la
// nature du code (code promo du pro d'abord, sinon carte cadeau / bon) ; le code de l'autre nature déjà
// posé est conservé. Retrait : PATCH `voucher` ou `promo` sans code.
export const UpdatePreReservationCodeRequestSchema = Type.Object({
  code: Type.String({
    minLength: 1,
    maxLength: 64,
    title: 'INPUT_TITLE_PRE_RESERVATION_VOUCHER_CODE',
    placeholder: 'INPUT_PLACEHOLDER_PRE_RESERVATION_VOUCHER_CODE',
  }),
});
export type UpdatePreReservationCodeRequestObject = Static<typeof UpdatePreReservationCodeRequestSchema>;

// ─── Response ─────────────────────────────────────────────────────────────────

export const PreReservationPrestationSummarySchema = Type.Object({
  name: Type.String(),
  quantity: Type.Number(),
  /* Nombre de personnes facturées sur la ligne, renseigné seulement au-delà d'une unité. C'est le
     multiplicateur appliqué au prix : sans lui, le client lit un total multiplié en face d'une
     ligne qui n'en porte aucune trace. */
  party_size: Type.Optional(Type.Integer({ minimum: 2 })),
  price_type: Type.Optional(PriceTypeSchema()),
  /* Récap de la confirmation (spec Parcours § 7.7, § 9 ligne 1), lu dans l'instantané de la réservation :
     formule quand la prestation en propose plusieurs, durée de la ligne, prix normal × unités facturées
     (avant remise ; absent sans prix). Formatés serveur. */
  formula: Type.Optional(Type.String()),
  duration: Type.Optional(Type.String()),
  price: Type.Optional(Type.String({ meta: { is_price: true } })),
  supplements: Type.Optional(
    Type.Array(
      Type.Object({
        name: Type.String(),
        /** Nombre de ce supplément sur la ligne, renseigné au-delà d'un seul. */
        quantity: Type.Optional(Type.Integer({ minimum: 2 })),
        price: Type.Optional(Type.String({ meta: { is_price: true } })),
      }),
    ),
  ),
});
export type PreReservationPrestationSummaryObject = Static<typeof PreReservationPrestationSummarySchema>;

export const PreReservationSlotSummarySchema = Type.Object({
  // Course VTC : legs du trajet (adresses/distance/durée) + classe de véhicule réservée
  // (snapshot). L'identité à l'unité (plaque/modèle) n'est plus exposée.
  ride_details: Type.Optional(Type.Array(AppointmentRideDetailsResponseSchema)),
  vehicle_name: Type.Optional(Type.String()),
  // Début / fin du créneau, formatés par le serveur depuis les instants `start_pro` / `end_pro` de la source.
  // Ces instants ne sont PAS exposés : passés par transformToSchema, ils sortiraient en heure murale du pro
  // (sans décalage) et seraient relus comme de l'UTC — le .ics et le lien Google se construisent côté
  // serveur sur les instants bruts (`booking-calendar.util`).
  start_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'start_pro', with_time: true } }),
  ),
  end_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'end_pro', with_time: true } })),
  pro_place_label: Type.Optional(Type.String()),
  pro_place_address_formatted: Type.Optional(Type.String()),
  customer_address_formatted: Type.Optional(Type.String()),
  team_member_firstname: Type.Optional(Type.String()),
  team_member_lastname: Type.Optional(Type.String()),
  team_member_picture: Type.Optional(Type.Array(CroppableImageResponseSchema)),
  team_members: Type.Optional(
    Type.Array(
      Type.Object({
        team_member_id: Type.Number(),
        firstname: Type.Optional(Type.String()),
        lastname: Type.Optional(Type.String()),
        picture: Type.Optional(Type.Array(CroppableImageResponseSchema)),
      }),
    ),
  ),
  prestations: Type.Array(PreReservationPrestationSummarySchema),
});
export type PreReservationSlotSummaryObject = Static<typeof PreReservationSlotSummarySchema>;

export const BookingConfirmationResponseSchema = Type.Object({
  reservation_uuid: Type.String(),
  payment_uuid: Type.Optional(Type.String()),
  reservation_status: Type.Optional(Type.Enum(ReservationStatus)),
  payment_status: Type.Optional(Type.Enum(ProPaymentStatus)),
  payment_type: PrestationPaymentTypeSchema(),
  voucher_code: Type.Optional(Type.String()),
  voucher_discount: Type.Optional(Type.String({ meta: { is_price: true } })),
  voucher_refundable_on_cancel: Type.Optional(Type.Boolean()),
  voucher_refund_info: Type.Optional(
    Type.Object({
      code: Type.String(),
      amount: Type.String({ meta: { is_price: true, source_key: 'amount_raw' } }),
      amount_raw: Type.Number(),
      is_refundable: Type.Boolean(),
    }),
  ),
  promo_code: Type.Optional(Type.String()),
  promo_code_label: Type.Optional(Type.String()),
  promo_discount: Type.Optional(Type.String({ meta: { is_price: true } })),
  payment_infos: PaymentInfosSchema,
  // Optionnel : `transformToSchema` sérialise un tableau vide en `null` — cas atteignable
  // (pré-résa expirée/annulée : appointments soft-deleted, ligne reservation conservée).
  slots: Type.Optional(Type.Array(PreReservationSlotSummarySchema)),
  customer_firstname: Type.String(),
  customer_lastname: Type.String(),
  customer_email: Type.String(),
  professional_name: Type.String(),
  professional_logo: Type.Optional(Type.Array(CroppableImageResponseSchema)),
  professional_email_contact: Type.String(),
  professional_phone_number: Type.Optional(Type.String()),
  note: Type.Optional(Type.String()),
  can_cancel: Type.Optional(Type.Boolean()),
  can_reschedule: Type.Optional(Type.Boolean()),
  // Le pro valide chaque réservation (`confirm_every_booking`) : un déplacement par le client
  // repasse la résa en attente de sa confirmation — la vue de gestion adapte ses textes.
  reschedule_requires_pro_confirmation: Type.Optional(Type.Boolean()),
  // Lot 4 Promotions : la remise de la réservation tient à son créneau (happy hours, dates du rendez-vous) ;
  // le report ne propose que les créneaux qui la gardent. Libellé de l'offre pour la phrase de sortie.
  reschedule_promotion_name: Type.Optional(Type.String()),
  // Réservation composée uniquement d'appointments RIDE : la vue de gestion adapte son rendu.
  is_ride: Type.Optional(Type.Boolean()),
  cancellation_reason: Type.Optional(Type.String()),
  cancelled_at: Type.Optional(Type.String({ format: 'date-time' })),
  cancelled_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'cancelled_at', with_time: true } }),
  ),
  booking_cancellation_refund_info: Type.Optional(BookingCancellationRefundInfoSchema),
  refunded_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'refunded_amount_raw' } })),
  refunded_amount_raw: Type.Optional(Type.Number()),
  non_refunded_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'non_refunded_amount_raw' } })),
  non_refunded_amount_raw: Type.Optional(Type.Number()),
  non_refundable_amount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'non_refundable_amount_raw' } }),
  ),
  non_refundable_amount_raw: Type.Optional(Type.Number()),
  cancellation_deadline: Type.Optional(Type.String({ format: 'date-time' })),
  cancellation_deadline_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'cancellation_deadline', with_time: true } }),
  ),
  instructions: Type.Optional(Type.Array(BookingInstructionPublicSchema)),
  // Récap de la page de gestion : même bloc que celui du tunnel, payé en ligne et reste sur place compris.
  amounts: Type.Optional(BookingAmountsSchema),
  // « Ajouter au calendrier » (P3) : lien Google Agenda ; le fichier .ics est servi par `GET :uuid/calendar.ics`.
  calendar_google_url: Type.Optional(Type.String()),
});
export type BookingConfirmationResponseObject = Static<typeof BookingConfirmationResponseSchema>;

// Schéma déplacé dans le module neutre `payment_infos.ts` (réutilisé par le devis VTC, sans créer
// de cycle professional ↔ pre_reservation). Alias conservés pour compat des imports existants.
export { PaymentInfosSchema as PreReservationPaymentInfosSchema } from './payment_infos';
export type { PaymentInfosObject as PreReservationPaymentInfosObject } from './payment_infos';

export const PreReservationResponseSchema = Type.Object({
  reservation_uuid: Type.String(),
  uuid: Type.Optional(Type.String()),
  payment_url: Type.Optional(Type.String()),
  confirmed: Type.Optional(Type.Boolean()),
  payment_type: PrestationPaymentTypeSchema(),

  voucher_code: Type.Optional(Type.String()),
  voucher_discount: Type.Optional(Type.String({ meta: { is_price: true } })),
  voucher_refundable_on_cancel: Type.Optional(Type.Boolean()),

  promo_code: Type.Optional(Type.String()),
  promo_code_label: Type.Optional(Type.String()),
  promo_discount: Type.Optional(Type.String({ meta: { is_price: true } })),
  // Promotion automatique annoncée au récap puis retirée une fois le client identifié (déjà venu, tag
  // absent, quota atteint) : la pré-réservation est créée sans elle, le tunnel l'explique (jamais bloquant).
  promotion_rejected_reason: Type.Optional(Type.Enum(BookingPromoIneligibilityReason)),
  promotion_rejected_label: Type.Optional(Type.String()),

  payment_infos: PaymentInfosSchema,
  without_voucher_payment_infos: Type.Optional(PaymentInfosSchema),
  // Récap du tunnel (remises, bons, à payer en ligne, reste sur place) : même bloc que `cart-summary`.
  amounts: Type.Optional(BookingAmountsSchema),

  booking_summary: Type.Optional(
    Type.Object({
      professional_name: Type.String(),
      note: Type.Optional(Type.String()),
      slots: Type.Array(PreReservationSlotSummarySchema),
    }),
  ),

  // Une entrée par ligne réservée, pour les `items` de l'événement de suivi `booking_confirmed`.
  // `quantity` = unités facturées (quantité × nombre de personnes quand la ligne est multipliée
  // par `party_size`) ; la somme des lignes vaut le total TTC avant bon.
  tracking_items: Type.Optional(Type.Array(TrackingItemSchema)),
  // Devise du pro (code ISO 4217, casse non garantie : cf. `resolveSiteEventCurrency`) : celle
  // des montants bruts ci-dessus, reprise par l'événement de suivi.
  currency: Type.Optional(Type.String()),

  cancellation_deadline: Type.Optional(Type.String()),
  cancellation_deadline_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'cancellation_deadline', with_time: true } }),
  ),
  expires_at: Type.Optional(Type.String()),
});
export type PreReservationResponseObject = Static<typeof PreReservationResponseSchema>;

// ─── Cancel Confirmed Booking ─────────────────────────────────────────────────

export const CancelConfirmedBookingRequestSchema = Type.Object({
  cancellation_reason: Type.Optional(
    Type.String({
      maxLength: 1000,
    }),
  ),
});
export type CancelConfirmedBookingRequestObject = Static<typeof CancelConfirmedBookingRequestSchema>;

/**
 * Résultat de la tentative de prélèvement automatique des frais d'annulation tardive
 * (CARD_SAVE : débit off-session de la carte enregistrée au moment de l'annulation).
 */
export enum LateCancelPenaltyChargeStatus {
  /** Pénalité débitée sur la carte enregistrée. */
  CHARGED = 'CHARGED',
  /** Tentative échouée (refus carte, erreur) — le pro peut retenter depuis la fiche paiement. */
  FAILED = 'FAILED',
  /** Pénalité due mais rien de tentable (aucune carte enregistrée exploitable). */
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export const CancelConfirmedBookingResponseSchema = Type.Object({
  uuid: Type.String(),
  reservation_uuid: Type.Optional(Type.String()),
  status: Type.Enum(ProPaymentStatus),
  refunded_amount: Type.String({ meta: { is_price: true } }),
  refunded_amount_raw: Type.Number(),
  non_refunded_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'non_refunded_amount_raw' } })),
  non_refunded_amount_raw: Type.Optional(Type.Number()),
  was_before_deadline: Type.Boolean(),
  // Absents si : annulation dans les délais, pénalité nulle, mode ≠ CARD_SAVE, ou résa déjà annulée.
  late_cancel_penalty_charge_status: Type.Optional(Type.Enum(LateCancelPenaltyChargeStatus)),
  late_cancel_penalty_amount: Type.Optional(
    Type.String({ meta: { is_price: true, source_key: 'late_cancel_penalty_amount_raw' } }),
  ),
  late_cancel_penalty_amount_raw: Type.Optional(Type.Number()),
});
export type CancelConfirmedBookingResponseObject = Static<typeof CancelConfirmedBookingResponseSchema>;

// ─── Reschedule Confirmed Booking ─────────────────────────────────────────────

export const RescheduleConfirmedBookingAvailabilityRequestSchema = Type.Object({
  start_date: Type.Optional(Type.String({ format: 'date-time' })),
  include_session_slots_below_party_size: IncludeSessionSlotsBelowPartySizeSchema,
});
export type RescheduleConfirmedBookingAvailabilityRequestObject = Static<
  typeof RescheduleConfirmedBookingAvailabilityRequestSchema
>;

export const RescheduleConfirmedBookingAvailabilityResponseSchema = BookableAvailabilityResponseSchema;
export type RescheduleConfirmedBookingAvailabilityResponseObject = Static<
  typeof RescheduleConfirmedBookingAvailabilityResponseSchema
>;

export const RescheduleConfirmedBookingRequestSchema = Type.Object({
  slots: Type.Array(BookableSlotItemSchema, { minItems: 1 }),
  reschedule_reason: Type.Optional(
    Type.String({
      maxLength: 1000,
    }),
  ),
});
export type RescheduleConfirmedBookingRequestObject = Static<typeof RescheduleConfirmedBookingRequestSchema>;

export const RescheduleConfirmedBookingResponseSchema = BookingConfirmationResponseSchema;
export type RescheduleConfirmedBookingResponseObject = Static<typeof RescheduleConfirmedBookingResponseSchema>;

// ─── Shift Reservation (pro drag & drop) ──────────────────────────────────────
// Décalage en bloc de toute la réservation : tous les appointments sont décalés
// du même offset (en minutes). Modèle « confiance pro » — pas de validation de
// disponibilité (comme le drag d'un RDV simple), juste l'application du delta.
// Le client reçoit l'email de modification dès que la date de la réservation change.
export const ShiftReservationRequestSchema = Type.Object({
  delta_minutes: Type.Integer(),
  // Sous-ensemble de créneaux à décaler (ex. « créneaux suivants » après un
  // redimensionnement dans l'agenda). Si absent → toute la réservation (cas drag & drop).
  appointment_ids: Type.Optional(Type.Array(Type.Number())),
});
export type ShiftReservationRequestObject = Static<typeof ShiftReservationRequestSchema>;
